import React, { useEffect, useState } from 'react';
import router from 'next/router';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Lazy, Mousewheel, Navigation, Pagination } from 'swiper/core';

import { bugsnag } from 'shared/utils/services/bugsnag';
import { BrandedContent } from 'homepage/components/brandView/BrandedContent';
import { BrandSpaceContent } from 'homepage/components/brandView/BrandSpaceContent';
import { BrandTabButtons } from 'homepage/components/BrandTabButtons';
import { computeSpaceDimensions } from 'brand/utils/computeSpaceDimensions';
import {
  DEFAULT_ASSET_HEIGHT_BEFORE_UI_RENDERS,
  DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS,
  SECTION_TYPES,
} from 'homepage/enums/contentConstants';
import { FeatureFlags } from 'featureFlags/constants';
import { filterSections } from 'brand/utils/filterSections';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { usePutBackHeaderOnPageChange } from 'shared/hooks/usePutBackHeaderOnPageChange';
import { useToggleHeaderOnScrollEvent } from 'shared/hooks/useToggleHeaderOnScrollEvent';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { useVideoLazyLoad } from 'homepage/hooks/useVideoLazyLoad';
import { VerticalScrollTab } from 'homepage/components/VerticalScrollBrandTab';
import { WRAPPER } from 'homepage/styles/constants';

import { IFeedHeroSection } from 'homepage/types/Feed';
import { ISpaceArticleSection } from 'shared/types/SpaceArticleSection';

import 'swiper/swiper-bundle.min.css';

SwiperCore.use([Lazy, Mousewheel, Navigation, Pagination]);

/**
 * [BrandedContentContainer]
 *  displays a simulated random assortment of branded assets (image & video)
 * @param {Object} heroes A list heroes to be split into groups of 4.
 */

interface IBrandedContentContainer {
  draftId?: number;
  draftUuid?: string;
  heroes: (IFeedHeroSection | ISpaceArticleSection)[] | [];
  noTopMargin?: boolean;
  drawerName?: string;
  isVisible: boolean;
}

const DEFAULT_ASPECT_RATIO = 1.8;

export const BrandedContentContainer = ({
  heroes = [],
  noTopMargin = false,
  draftId = null,
  draftUuid = null,
  drawerName = '',
  isVisible,
}: IBrandedContentContainer) => {
  useVideoLazyLoad();
  useToggleHeaderOnScrollEvent(true);
  usePutBackHeaderOnPageChange();

  const enableButtons = useFeatureFlag(FeatureFlags.WEB_ENABLE_TOGGLE_NEXT_PREV_BRAND_FEED);
  const enableLightButtons = useFeatureFlag(
    FeatureFlags.WEB_ENABLE_LIGHT_NEXT_PREV_BUTTONS_BRAND_FEED,
  );

  const { isMobile, windowSize } = useWindowSize();

  const isMounted = useIsMounted();

  const MAX_LOADABLE_RANGE = 2;

  const aspectRatio =
    heroes?.[0]?.articles?.[0]?.sections?.[0]?.aspectRatio || DEFAULT_ASPECT_RATIO;
  const mobileAspectRatio = heroes?.[0]?.articles?.[1]?.sections?.[0]?.aspectRatio || aspectRatio;

  const [canvasHeight, setCanvasHeight] = useState(DEFAULT_ASSET_HEIGHT_BEFORE_UI_RENDERS);
  const [canvasWidth, setCanvasWidth] = useState(DEFAULT_ASSET_WIDTH_BEFORE_UI_RENDERS);
  const [firstVideo, setIsFirstVideo] = useState(null);
  const [renderSpaces, setRenderSpaces] = useState(false);
  const [swiper, setSwiper] = useState<SwiperCore>(null);
  const [swiperActiveIndex, setSwiperActiveIndex] = useState(0);

  const generateProps = (item) => {
    const topLevelArticle = heroes?.[0]?.articles?.[isMobile ? 1 : 0];
    const moduleTrackingData = {
      collection_id: null,
      collection_slug: null,
      event_context: 'brand_feed',
      draft_id: draftUuid || String(draftId),
      section_id: topLevelArticle?.uuid || String(topLevelArticle?.id),
      section_position: 0,
      section_type: SECTION_TYPES.SPACE_ARTICLE_SECTION,
      section_name: topLevelArticle?.subject,
    };

    if (isMobile) {
      return {
        drawerName,
        key: item?.uuid || item?.id,
        section: item,
        maxHeight: canvasHeight,
        maxWidth: canvasWidth,
        moduleTrackingData,
        virtualMaxHeight: canvasHeight,
        virtualMaxWidth: canvasWidth,
        segment_id: item?.segmentId,
      };
    }
    return {
      drawerName,
      key: item?.uuid || item?.id,
      section: item,
      maxWidth: canvasWidth,
      moduleTrackingData,
      virtualMaxWidth: canvasWidth,
      segment_id: item?.segmentId,
    };
  };

  const handleSlideChange = (swipercore) => {
    const { activeIndex } = swipercore;
    setSwiperActiveIndex(activeIndex);
  };

  const handleClickNext = () => {
    if (swiperActiveIndex < heroes?.length - 1) {
      swiper.slideTo(swiperActiveIndex + 1);
    }
  };

  const handleClickPrevious = () => {
    if (swiperActiveIndex > 0) {
      swiper.slideTo(swiperActiveIndex - 1);
    }
  };

  const handleViewportSpecificContent = (asset) => {
    if (!asset) return false;

    const { aspect } = asset;

    const isPortrait = aspect < 1;

    if ((isPortrait && !isMobile) || (!isPortrait && isMobile)) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (isMounted()) {
      try {
        if (swiper?.params) {
          swiper?.update();
          swiper?.updateAutoHeight();
        }
      } catch (error) {
        bugsnag(error, { context: 'Error resizing swiper of spaces' });
      }

      setRenderSpaces(true);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (isMounted() && swiper?.params) {
        try {
          const { height, width }: { height: number; width: number } = computeSpaceDimensions(
            windowSize?.height,
            windowSize?.width,
            isMobile ? mobileAspectRatio : aspectRatio,
          );

          setCanvasHeight(height + 1);
          setCanvasWidth(width);
          swiper?.update();
          swiper?.updateAutoHeight();
          swiper?.updateSize();
        } catch (error) {
          bugsnag(error, { context: 'Error resizing swiper of spaces' });
        }
      }
    };

    document.addEventListener('resize', handleResize);
    router.events.on('routeChangeComplete', handleResize);
    return () => {
      document.removeEventListener('resize', handleResize);
      router.events.off('routeChangeComplete', handleResize);
    };
  });

  useEffect(() => {
    if (isMounted() && windowSize?.width) {
      try {
        const { height, width }: { height: number; width: number } = computeSpaceDimensions(
          windowSize?.height,
          windowSize?.width,
          isMobile ? mobileAspectRatio : aspectRatio,
        );
        setCanvasHeight(height + 1);
        setCanvasWidth(width);
      } catch (error) {
        bugsnag(error, { context: 'Error resizing swiper of spaces' });
      }
    }
  }, [isMobile, windowSize, aspectRatio, mobileAspectRatio, isMounted]);

  const filteredArticleSections = filterSections(heroes, isMobile);

  return (
    <div>
      <BrandedContentContainer.Wrapper $noTopMargin={noTopMargin}>
        {renderSpaces && canvasWidth && heroes?.length > 1 && (
          <Swiper
            className="mySwiper"
            direction="horizontal"
            observer
            observeParents
            parallax
            hashNavigation={{
              watchState: true,
            }}
            initialSlide={0}
            lazy={{
              checkInView: true,
              loadOnTransitionStart: true,
            }}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            modules={[Lazy, Pagination, Mousewheel, Navigation]}
            mousewheel
            keyboard={{
              enabled: true,
            }}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            centeredSlides
            onSlideChange={handleSlideChange}
            onSwiper={(s) => setSwiper(s)}
            slidesPerView="auto"
            touchStartPreventDefault={false}
            speed={600}
          >
            {filteredArticleSections?.map((item, index: number) =>
              item?.type === SECTION_TYPES.SPACE_ARTICLE_SECTION ? (
                <SwiperSlide id={`space-${index}`} key={item?.uuid || item?.id}>
                  <BrandedContentContainer.SwiperMinSizeContainer>
                    {Math.abs(swiper?.activeIndex - index) < MAX_LOADABLE_RANGE ? (
                      <BrandSpaceContent
                        {...(item && generateProps(item))}
                        sectionIndex={index}
                        draftId={draftId}
                        draftUuid={draftUuid}
                      />
                    ) : (
                      <BrandedContentContainer.TempSpaceHolder
                        $height={canvasHeight}
                        $width={canvasWidth}
                      />
                    )}
                  </BrandedContentContainer.SwiperMinSizeContainer>
                </SwiperSlide>
              ) : (
                <>
                  {handleViewportSpecificContent(item?.pictures?.[0] || item?.videos?.[0]) && (
                    <SwiperSlide id={`space-${index}`} key={item?.uuid || item?.id}>
                      <BrandedContentContainer.SwiperMinSizeContainer>
                        <BrandedContent
                          key={item?.uuid || item?.id}
                          drawerName={drawerName}
                          {...item}
                          index={index}
                          firstVideo={firstVideo}
                          setFirstVideo={setIsFirstVideo}
                          canvasHeight={canvasHeight}
                          canvasWidth={canvasWidth}
                        />
                      </BrandedContentContainer.SwiperMinSizeContainer>
                    </SwiperSlide>
                  )}
                </>
              ),
            )}
          </Swiper>
        )}
        {renderSpaces &&
          canvasWidth &&
          filteredArticleSections?.length > 1 &&
          isVisible &&
          enableButtons && (
            <BrandTabButtons
              handleClickNext={handleClickNext}
              handleClickPrevious={handleClickPrevious}
              isLight={enableLightButtons}
              numOfHeroes={filteredArticleSections?.length}
              swiper={swiper}
              swiperActiveIndex={swiperActiveIndex}
            />
          )}
        {filteredArticleSections?.length === 1 && (
          <VerticalScrollTab
            draftId={draftId}
            draftUuid={draftUuid}
            drawerName={drawerName}
            firstVideo={firstVideo}
            heroes={heroes}
            isMobile={isMobile}
            setIsFirstVideo={setIsFirstVideo}
          />
        )}
      </BrandedContentContainer.Wrapper>
    </div>
  );
};

BrandedContentContainer.Wrapper = styled.div<{ $noTopMargin: boolean }>`
  ${({ $noTopMargin }) => $noTopMargin && `margin-top: 56px`};
  width: ${WRAPPER};
  max-width: 100vw;

  min-height: 100vh;
  overflow: hidden;

  ${hideScrollbars}

  /* Fix for COMM-211 where the Space appears above the header panel*/
  #space-container {
    z-index: ${({ theme }) => theme.zIndex.homepageBrandContent};
  }
`;

BrandedContentContainer.SwiperMinSizeContainer = styled.div`
  align-items: flex-start;
  display: flex;
  font-size: 36px;
  height: 100%;
  justify-content: center;
  min-height: 100%;
  overflow: hidden;
  width: 100vw;
  max-width: 100vw;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    align-items: center;
  }
`;

BrandedContentContainer.TempSpaceHolder = styled.div<{ $height: number; $width: number }>`
  min-height: ${({ $height }) => $height}px;
  min-width: ${({ $width }) => $width}px;
`;
